import { classifyWaterQuantityRole, waterNumericTokens, type WaterQuantityRole } from '../water/waterQuantityRoles';
import type { DocumentEvidenceFact } from '../../types/siteEvidence';
import type { SiteDocument } from '../../types/siteRag';
import { attributionFromSegment, formatAttribution } from './evidenceAttribution';
import { documentBodyText, pageForCharOffset } from '../documentBody';

/** Full source-quoted ledger retained in the GCS SED for every downstream consumer. */
export function collectSedDocumentFacts(docs: readonly SiteDocument[]): DocumentEvidenceFact[] {
  const facts: DocumentEvidenceFact[] = [];
  const seen = new Set<string>();
  for (const doc of docs) {
    if (doc.includeInSed === false || doc.deletedAt || doc.archived) continue;
    const body = documentBodyText(doc);
    for (const segment of doc.topicSegments ?? []) {
      for (const fact of segment.facts ?? []) {
        // Recheck the source after reload; stale/mismatched offsets do not become SED evidence.
        if (body.slice(fact.charStart, fact.charEnd) !== fact.sourceQuote) continue;
        const key = `${doc.id}:${fact.charStart}:${fact.label}:${fact.value}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const page = pageForCharOffset(doc.pages, fact.charStart);
        const attribution = attributionFromSegment(segment.attribution);
        const visual = doc.visualReadings?.some(r => [r.transcription ?? '', r.narrative, ...r.features, ...r.legend].some(t => t.includes(fact.sourceQuote)));
        facts.push({
          ...fact, documentId: doc.id, documentName: doc.fileName,
          ...(page !== undefined ? { page } : {}),
          sedSections: segment.sedSections, requirementIds: segment.requirementIds,
          confidence: Math.min(segment.confidence, visual ? 0.6 : 0.7),
          channel: visual ? 'vision' : 'document',
          ...(attribution ? { attribution } : {}),
          adoption: 'source-statement',
        });
      }
    }
  }
  return facts;
}

const FACTS_HEADER = '## SOURCE-QUOTED DOCUMENT FACTS';
const FACTS_SCOPE = 'These are source statements with scope and provenance, available to financial, power, generation and other analyses. They are not adopted design selections or financial assumptions. Cite the source; retain other-project, date and boundary qualifications.';
interface FactPromptRow { documentId: string; text: string }

/** Complete rows only: never cut a quote, scope qualification or citation anchor. */
function projectFactRows(rows: readonly FactPromptRow[], totalFacts: number, totalDocuments: number, maxChars: number): string {
  const lines = [FACTS_HEADER, FACTS_SCOPE];
  const budget = Number.isFinite(maxChars) ? Math.max(0, Math.floor(maxChars)) : 30000;
  const documents = new Map<string, string[]>();
  for (const row of rows) {
    const group = documents.get(row.documentId) ?? [];
    group.push(row.text);
    documents.set(row.documentId, group);
  }
  // One fact per document per round, preserving the supplied order within
  // each source. A long PDF cannot consume the prefix before utility letters.
  const groups = [...documents.entries()];
  const ordered: FactPromptRow[] = [];
  const rounds = Math.max(0, ...groups.map(([, group]) => group.length));
  for (let round = 0; round < rounds; round++) {
    for (const [documentId, group] of groups) if (group[round] !== undefined) ordered.push({ documentId, text: group[round]! });
  }
  const all = [...lines, ...ordered.map(row => row.text)].join('\n');
  if (all.length <= budget && rows.length === totalFacts) return all;
  const omission = (kept: number, represented: number) => `Prompt shows ${kept}/${totalFacts} facts across ${represented}/${totalDocuments} documents. Unshown facts are unassessed, not absent. The complete structured ledger is stored in the SED; retrieve further source passages for the relevant report section.`;
  // Reserve the longest possible notice and count separators too, so the
  // omission disclosure never silently exceeds the caller's prompt budget.
  const available = budget - omission(totalFacts, totalDocuments).length - 1;
  let chars = lines.join('\n').length;
  if (chars > available) return `Prompt omits ${totalFacts} facts; these are unassessed, not absent. The complete structured ledger is stored in the SED.`.slice(0, budget);
  let kept = 0;
  const represented = new Set<string>();
  for (const row of ordered) {
    if (chars + 1 + row.text.length > available) continue;
    lines.push(row.text); chars += 1 + row.text.length; kept++;
    represented.add(row.documentId);
  }
  lines.push(omission(kept, represented.size));
  return lines.join('\n');
}

const WATER_ROLE_LABELS: Record<WaterQuantityRole, string> = {
  'site-allocation': 'Whole-site water service allocation',
  'facility-demand-average': 'Average whole-site water demand',
  'facility-demand-peak': 'Peak whole-site water demand',
  'cooling-consumption': 'Cooling/process consumptive use',
  'consumptive-use-limit': 'Consumptive-use limit',
  'withdrawal-makeup': 'Water withdrawal or makeup',
  'firm-utility-delivery': 'Firm utility water delivery',
  'allocation-margin': 'Whole-site water allocation margin',
  'utility-system-operating': 'Utility-system operating water quantity',
};

/** Model labels cannot rename a quoted water role. Bind to the exact printed
 * rate; ambiguous roles/duplicate magnitudes stay unclassified. Full original
 * facts remain immutable in the ledger for review. */
function promptFactLabel(fact: DocumentEvidenceFact): string {
  const printed = `${fact.value} ${fact.unit ?? ''}`.trim();
  const rate = /^(\d[\d,]*(?:\.\d+)?)\s*(?:million\s*)?(?:MGD|gpd|gal(?:lons?)?\s*(?:\/|per)\s*day|AFY|acre[- ]feet\s*(?:\/|per)\s*year)$/i.exec(printed);
  if (!rate) return fact.label;
  const magnitude = Number(rate[1]!.replaceAll(',', ''));
  const matches = Number.isFinite(magnitude) ? waterNumericTokens(fact.sourceQuote).filter(token => token.value === magnitude
    && /^\s*(?:million\s*)?(?:MGD|gpd|gal(?:lons?)?\s*(?:\/|per)\s*day|AFY|acre[- ]feet\s*(?:\/|per)\s*year)\b/i.test(fact.sourceQuote.slice(token.end))) : [];
  const roles = matches.map(token => classifyWaterQuantityRole(fact.sourceQuote, token.start, token.end));
  const role = roles[0];
  return role && roles.every(candidate => candidate === role)
    ? WATER_ROLE_LABELS[role] : 'Source-stated water quantity (role unverified)';
}

/** Bounded prompt projection; the full ledger stays available on the SED. */
export function formatSedDocumentFacts(facts: readonly DocumentEvidenceFact[], maxChars = 30000): string {
  if (!facts.length) return '';
  const rows = facts.map((fact): FactPromptRow => {
    const attribution = formatAttribution(fact.attribution);
    const anchor = `doc://${fact.documentId}${fact.page ? `#page=${fact.page}&start=${fact.charStart}&end=${fact.charEnd}` : `#start=${fact.charStart}&end=${fact.charEnd}`}`;
    // A fixed anchor prefix identifies the source without parsing model prose.
    // JSON quoting preserves the exact source span, including embedded newlines
    // or markdown headers, on one physical line for later excerpt compaction.
    const text = `- ${anchor} | ${promptFactLabel(fact)}: ${fact.value}${fact.unit ? ` ${fact.unit}` : ''}. Source: ${fact.documentName}${fact.page ? ` p.${fact.page}` : ''}${attribution ? `; ${attribution}` : ''}${fact.channel === 'vision' ? '; visual screening observation' : ''}. Quote: ${JSON.stringify(fact.sourceQuote)}`;
    return { documentId: fact.documentId, text: text.replace(/\r/g, '\\r').replace(/\n/g, '\\n') };
  });
  return projectFactRows(rows, facts.length, new Set(facts.map(fact => fact.documentId)).size, maxChars);
}

/** Re-budget only our losslessly framed projection; unknown/legacy prose is
 * never guessed into source rows. Existing original-ledger counts survive a
 * second (reviewer) projection, so omitted facts cannot masquerade as absent. */
export function compactSedDocumentFactsText(text: string, maxChars: number): string | undefined {
  const lines = text.trimEnd().split('\n');
  if (lines[0] !== FACTS_HEADER || lines[1] !== FACTS_SCOPE) return undefined;
  const rows: FactPromptRow[] = [];
  let totals: { facts: number; documents: number } | undefined;
  for (const line of lines.slice(2)) {
    if (!line.trim()) continue;
    const row = /^- doc:\/\/([^#\s]+)#(?:page=\d+&)?start=\d+&end=\d+ \| /.exec(line);
    if (row && !totals) { rows.push({ documentId: row[1]!, text: line }); continue; }
    const notice = /^Prompt shows (\d+)\/(\d+) facts across (\d+)\/(\d+) documents\. Unshown facts are unassessed, not absent\./.exec(line);
    if (!notice || totals || Number(notice[1]) !== rows.length) return undefined;
    totals = { facts: Number(notice[2]), documents: Number(notice[4]) };
  }
  if (!rows.length || (totals && totals.facts < rows.length)) return undefined;
  return projectFactRows(rows, totals?.facts ?? rows.length, totals?.documents ?? new Set(rows.map(row => row.documentId)).size, maxChars);
}
